import React, { FC } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Box, Button, FormControlLabel, Stack, Switch } from '@mui/material';
import { useSnackbar } from 'notistack';

import { useAgentDebugLogs } from 'src/contexts/AgentDebugLogsContext';
import { getJSONWebToken } from 'src/contexts/auth0/utils';

import useAuth from 'src/hooks/useAuth';

import DevOverrides from 'src/utils/DevOverrides';

const localBackendUrl = `http://localhost:8080`;

const BrowserTab: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { userInfo } = useAuth();
  const { showDebugLogs, toggleDebugLogs } = useAgentDebugLogs();
  const handleCopyBearerToken = async () => {
    const token = await getJSONWebToken();
    navigator.clipboard.writeText(`Bearer ${token}`);
    enqueueSnackbar(t('hiring-dashboard-dev-tools:dev-tools.browser.bearerTokenCopied'), { variant: 'success' });
  };

  const orgName = userInfo?.orgName ?? '';
  const orgId = userInfo?.orgId ?? '';
  const employeeId = userInfo?.employeeId ?? '';
  const employeeName = userInfo?.employeeName ?? '';
  const userId = userInfo?.userId ?? '';
  const userName = userInfo?.userName ?? '';
  const handleCopyOrgId = async () => {
    navigator.clipboard.writeText(orgId);
    enqueueSnackbar(t('hiring-dashboard-dev-tools:dev-tools.browser.orgIdCopied'), { variant: 'success' });
  };
  const handleCopyEmployeeId = async () => {
    navigator.clipboard.writeText(employeeId);
    enqueueSnackbar(t('hiring-dashboard-dev-tools:dev-tools.browser.employeeIdCopied'), { variant: 'success' });
  };
  const handleCopyUserId = async () => {
    navigator.clipboard.writeText(userId);
    enqueueSnackbar(t('hiring-dashboard-dev-tools:dev-tools.browser.userIdCopied'), { variant: 'success' });
  };

  return (
    <Stack useFlexGap spacing={1}>
      <Button
        id="app-dev-tools-modal-browser-copy-bearer-token"
        data-testid="app-dev-tools-modal-browser-copy-bearer-token"
        onClick={handleCopyBearerToken}
      >
        {t('hiring-dashboard-dev-tools:dev-tools.browser.copyBearerToken')}
      </Button>

      <Box mt={3}>
        <Stack spacing={1}>
          <Button
            id="app-dev-tools-modal-browser-copy-org-id"
            data-testid="app-dev-tools-modal-browser-copy-org-id"
            onClick={handleCopyOrgId}
          >
            {t('hiring-dashboard-dev-tools:dev-tools.browser.copyOrgId', { orgName })}
          </Button>
          <Button
            id="app-dev-tools-modal-browser-copy-employee-id"
            data-testid="app-dev-tools-modal-browser-copy-employee-id"
            onClick={handleCopyEmployeeId}
          >
            {t('hiring-dashboard-dev-tools:dev-tools.browser.copyEmployeeId', { employeeName })}
          </Button>
          <Button
            id="app-dev-tools-modal-browser-copy-user-id"
            data-testid="app-dev-tools-modal-browser-copy-user-id"
            onClick={handleCopyUserId}
          >
            {t('hiring-dashboard-dev-tools:dev-tools.browser.copyUserId', { userName })}
          </Button>
        </Stack>
      </Box>

      <Box mt={3}>
        <Stack spacing={1}>
          <Button
            id="app-dev-tools-modal-browser-clear-local-storage"
            data-testid="app-dev-tools-modal-browser-clear-local-storage"
            color="error"
            onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}
          >
            {t('hiring-dashboard-dev-tools:dev-tools.browser.clearLocalStorage')}
          </Button>
          <Button
            id="app-dev-tools-modal-browser-clear-session-storage"
            data-testid="app-dev-tools-modal-browser-clear-session-storage"
            color="error"
            onClick={() => {
              sessionStorage.clear();
              window.location.reload();
            }}
          >
            {t('hiring-dashboard-dev-tools:dev-tools.browser.clearSessionStorage')}
          </Button>
        </Stack>
      </Box>

      <Box>
        <FormControlLabel
          labelPlacement="start"
          control={
            <Switch
              defaultChecked={!!DevOverrides.getLocalBackendUrl()}
              inputProps={
                {
                  id: 'app-dev-tools-modal-browser-use-local-backend',
                  'data-testid': 'app-dev-tools-modal-browser-use-local-backend',
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  DevOverrides.setLocalBackendUrl(localBackendUrl);
                } else {
                  DevOverrides.removeLocalBackendUrl();
                }
                window.location.reload();
              }}
            />
          }
          label={t('hiring-dashboard-dev-tools:dev-tools.browser.useLocalBackend')}
        />
      </Box>

      <Box>
        <FormControlLabel
          labelPlacement="start"
          control={
            <Switch
              defaultChecked={DevOverrides.getIsBatchDisabled()}
              inputProps={
                {
                  id: 'app-dev-tools-modal-browser-disable-batch-queries',
                  'data-testid': 'app-dev-tools-modal-browser-disable-batch-queries',
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  DevOverrides.setIsBatchDisabled(true);
                } else {
                  DevOverrides.removeIsBatchDisabled();
                }
                window.location.reload();
              }}
            />
          }
          label={t('hiring-dashboard-dev-tools:dev-tools.browser.disableBatchQueries')}
        />
      </Box>

      <Box>
        <FormControlLabel
          labelPlacement="start"
          control={
            <Switch
              checked={showDebugLogs}
              onChange={toggleDebugLogs}
              inputProps={
                {
                  id: 'app-dev-tools-modal-browser-agent-debug-logs',
                  'data-testid': 'app-dev-tools-modal-browser-agent-debug-logs',
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
            />
          }
          label="Show agent debug logs"
        />
      </Box>
    </Stack>
  );
};

export default BrowserTab;
